import React, { useEffect, useRef, useState } from 'react';
import { Close } from './Glyph';
import { supabase } from '../lib/supabase';
import { searchClause } from '../pages/catalog/catalogData';
import Dialog from './Dialog';
import { IconPin } from './dialogIcons';
import { pressable } from '../lib/pressable';

// Picking up a location nobody gave you.
//
// This used to be "Add Account": it searched referral sources and warehouses,
// invented an account row out of whichever one you picked, and hung a brand
// new count sheet off it. None of that is needed any more. Opening a cycle
// already makes a sheet for every warehouse, so the only thing missing is your
// name against one. Search the warehouses, pick one, and its sheet - with last
// count's item list already on it - appears in My Counts.
export default function AddLocationModal({ profile, cycle, toast, onClose, onSuccess }) {
  const [searchVal, setSearchVal]         = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]         = useState(false);
  const [selected, setSelected]           = useState(null);
  const [notes, setNotes]                 = useState('');
  const [saving, setSaving]               = useState(false);
  const searchTimer                       = useRef(null);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (!searchVal || searchVal.length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      const q = searchVal.trim();
      // Inactive locations are still searchable. A rep standing in one needs to
      // be able to count it; whether it is still open is the admin's call.
      const { data } = await supabase
        .from('warehouses')
        .select('id, name, display_name, region_prefix, city, state, phone, is_do_not_use, region:regions(name)')
        // The name, the full name and the city, as the box promises, with a
        // typed % or _ taken literally rather than as a wildcard (the audit
        // after the rebrand, 19 September 2026: it searched the name only).
        .or(searchClause(['display_name', 'name', 'city'], q))
        .order('display_name')
        .limit(10);
      setSearchResults(data || []);
      setSearching(false);
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [searchVal]);

  function selectResult(r) {
    setSelected(r);
    setSearchVal('');
    setSearchResults([]);
  }

  async function submit() {
    if (!selected) { toast.error('Search for a location and pick one first.'); return; }
    if (!cycle?.id) { toast.error('There is no open count cycle to add a location to.'); return; }
    setSaving(true);

    const label = selected.display_name || selected.name;

    const { data: countId, error } = await supabase.rpc('claim_warehouse_for_cycle', {
      p_cycle_id:    cycle.id,
      p_warehouse_id: selected.id,
    });
    if (error) { toast.error('Could not add that location: ' + error.message); setSaving(false); return; }

    // The admin gets told, the same as before - a rep picking up a location
    // nobody assigned is worth someone looking at, even though nothing is wrong.
    const { error: todoErr } = await supabase.from('todos').insert({
      title:       'Location picked up: ' + label,
      description: (profile?.full_name || 'A rep') + ' added ' + label
                   + ' to their counts for "' + cycle.name + '".'
                   + (selected.region?.name ? ' Region: ' + selected.region.name + '.' : '')
                   + (notes ? ' Notes: ' + notes : ''),
      priority:    'normal',
      todo_type:   'general',
      warehouse_id: selected.id,
      count_id:    countId || null,
      rep_id:      profile?.id || null,
      is_complete: false,
    });
    if (todoErr) { toast.error('Added, but the admin could not be notified: ' + todoErr.message); }
    else toast.success(label + ' is on your list. Admin notified.');

    setSaving(false);
    onSuccess();
    onClose();
  }

  const noResults = searchVal.length >= 2 && !searching && searchResults.length === 0;

  return (
    <Dialog
      kind="info" icon={<IconPin />}
      title="Add a location to your list"
      says="It goes onto your count list right away, with whatever it came to last time already on it. An admin is told."
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !selected}>
            {saving ? 'Adding...' : 'Add it and start counting'}
          </button>
        </>
      }
    >


          {/* Search */}
          <div className="form-lbl" style={{ marginTop: 0 }}>Search for a location</div>
          {selected ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--selected)', border: '1.5px solid var(--action)', borderRadius: 'var(--oh-radius-control)', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{selected.display_name || selected.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {[selected.city, selected.state].filter(Boolean).join(', ')}
                  {selected.phone && <span>{selected.phone}</span>}
                  {selected.region?.name && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--success-bg)', color: 'var(--success-ink)', padding: '1px 7px', borderRadius: 'var(--oh-radius-pill)' }}>{selected.region.name}</span>}
                </div>
              </div>
              {/* A button, not a span with a click on it: a keyboard could not reach
                  this and nothing said what it did. Its cross was a typed U+00D7. */}
              <button type="button" onClick={() => setSelected(null)} aria-label="Clear the chosen location"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)',
                         display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                         width: 32, height: 32, borderRadius: 'var(--oh-radius-xs)', flexShrink: 0 }}>
                <Close size={15} />
              </button>
            </div>
          ) : (
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <input
                className="form-inp"
                placeholder="Search by name or city..."
                value={searchVal}
                onChange={e => { setSearchVal(e.target.value); setSelected(null); }}
                autoFocus
              />
              {searching && <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>Searching...</div>}
              {searchResults.length > 0 && (
                <div style={{ border: '1.5px solid var(--action)', borderRadius: 'var(--oh-radius-control)', marginTop: 4, boxShadow: 'var(--depth-lift)', maxHeight: 300, overflowY: 'auto', background: 'var(--surface-card)' }}>
                  {searchResults.map((r, i) => (
                    <div key={r.id} {...pressable(() => selectResult(r))}
                      style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: i < searchResults.length - 1 ? '1px solid var(--border-soft)' : 'none', background: 'var(--surface-data)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-data)'}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{r.display_name || r.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {[r.city, r.state].filter(Boolean).join(', ')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                        {r.is_do_not_use && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--warning-bg)', color: 'var(--warning-ink)', padding: '2px 8px', borderRadius: 'var(--oh-radius-pill)' }}>Do Not Use</span>}
                        {r.region?.name && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--success-bg)', color: 'var(--success-ink)', padding: '2px 8px', borderRadius: 'var(--oh-radius-pill)' }}>{r.region.name}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {noResults && (
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-secondary)', padding: '8px 4px' }}>
                  Nothing matches "{searchVal}". Locations come across from Claimsoft - if this one is missing, your admin adds it there.
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="form-lbl">Notes for the admin <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>(optional)</span></div>
          <textarea className="form-ta" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional context for the admin..." />

    </Dialog>
  );
}
